'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Lock, CheckCircle2, Clock, ExternalLink } from 'lucide-react';
import { useLang } from '@/lib/i18n/LanguageContext';
import { COMMUNITY_KINDS, FXDOCTOR_GOLD_CHANNEL_ID, FXDOCTOR_GOLD_STRIPE_LINK, type CommunityKind, type CommunityRequest } from '@/lib/community';
import { timeAgoShort } from '@/lib/dates';
import LiveEventBanner from '@/components/chat/LiveEventBanner';

interface PreviewMsg { id: string; text: string | null; imageUrl: string | null; createdAt: string }
interface Preview { symbol: string; username: string; userImage: string | null; messages: PreviewMsg[] }

// What a non-member sees on /live (rewritten 2026-09-07 per owner: this page IS the login/
// entry point for FX Doctor's "Zajednica" — it must explain and advertise Zajednica itself,
// not present a menu of unrelated products. FX Doctor's Gold (XAUUSD signals) is a genuinely
// separate ShowMeTrade product whose subscribers happen to also get /live — kept, but as a
// small footnote, not an equal-weight "way in". Zajednica is registered AND PAID entirely on
// fxdoctor.pro; this page only grants access afterwards, via the request form below.
export default function CommunityGate({
  signedIn, request, authHeaders, onSubmitted,
}: { signedIn: boolean; request: CommunityRequest | null; authHeaders: () => Record<string, string>; onSubmitted: () => void }) {
  const { t } = useLang();
  const [kind, setKind] = useState<CommunityKind>('ZAJEDNICA');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const hint = COMMUNITY_KINDS.find((k) => k.value === kind)?.hint ?? '';

  // Real teaser — @fxdoctor's own latest room post (owner idea, 2026-09-07): the rooms
  // themselves are members-only, so this is the one live, honest glimpse a visitor gets.
  const [preview, setPreview] = useState<Preview | null>(null);
  useEffect(() => {
    let alive = true;
    fetch('/api/live/preview').then((r) => r.json()).then((d) => { if (alive) setPreview(d?.preview ?? null); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const res = await fetch('/api/community/me', { method: 'POST', headers: authHeaders(), credentials: 'include', body: JSON.stringify({ kind, reference, note }) });
      const d = await res.json();
      if (!res.ok) { setError(t(d.error || 'Something went wrong. Please try again.')); return; }
      onSubmitted();
    } catch { setError(t('Something went wrong. Please try again.')); } finally { setBusy(false); }
  }

  const included = [
    { t: t('Mentorski kurs'), d: t('graduates'), href: 'https://fxdoctor.pro/mentorski-kurs' },
    { t: t('Master 1-na-1'), d: t('graduates'), href: 'https://fxdoctor.pro/master' },
  ];

  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      <LiveEventBanner />
      <div className="p-5 sm:p-7 max-w-[720px]">
        <div className="inline-flex items-center gap-2 text-[11px] font-extrabold uppercase" style={{ letterSpacing: '1.2px', color: 'var(--accent)' }}>
          <Lock size={12} /> {t('Members only')}
        </div>
        <h2 className="font-heading mt-2 text-[26px] sm:text-[24px]" style={{ fontWeight: 800, color: 'var(--text-primary)' }}>{t('FX Doctor Zajednica')}</h2>
        <p className="mt-2 text-[15px] sm:text-sm" style={{ color: 'var(--text-secondary)', lineHeight: 1.55 }}>
          {t('The paid coaching community behind fxdoctor.pro — this page is its members-only room. Twice-monthly live setup reviews, a weekly analysis video, a fixed Thursday Q&A window and a full archive of every session. Not signals — you learn to decide, then trade your own idea.')}
        </p>

        {preview && (
          <div className="mt-4 rounded-2xl p-4" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
            <div className="text-[11px] font-extrabold uppercase mb-2.5" style={{ letterSpacing: '1.2px', color: 'var(--text-muted)' }}>
              {t('A peek inside')} · {preview.symbol}
            </div>
            <div className="flex items-start gap-2.5">
              {preview.userImage ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={preview.userImage} alt={preview.username} className="w-8 h-8 rounded-full object-cover shrink-0" />
              ) : (
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0" style={{ background: 'var(--accent-muted)', color: 'var(--accent)' }}>
                  {preview.username?.[0]?.toUpperCase() ?? '?'}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{preview.username}</span>
                  <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{timeAgoShort(preview.messages[preview.messages.length - 1].createdAt)}</span>
                </div>
                <div className="mt-1.5 flex flex-col gap-2">
                  {preview.messages.map((m) => (
                    <div key={m.id}>
                      {m.text && (
                        <div className="inline-block rounded-2xl px-3 py-1.5 text-sm" style={{ background: 'var(--inverse-bg)', color: '#fff' }}>{m.text}</div>
                      )}
                      {m.imageUrl && (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={m.imageUrl} alt="" className="mt-1 rounded-xl w-full" style={{ maxHeight: 260, objectFit: 'cover', border: '1px solid var(--border)' }} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-5 rounded-2xl p-5" style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)' }}>
          <div className="text-[16px] sm:text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{t('100 €/month, or 1 000 €/year')}</div>
          <p className="text-[14px] sm:text-xs mt-1" style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            {t('A PU Prime account balance under the FX Doctor link lowers it — 75 € at 500 €+, 50 € at 1 000 €+, free at 2 500 €+.')}
          </p>
          <Link href="https://fxdoctor.pro/zajednica" target="_blank" className="btn-primary inline-flex items-center gap-1.5 mt-3 px-4 py-2.5 text-[14px] sm:text-sm">
            {t('Join on fxdoctor.pro →')}
          </Link>
          <p className="text-[13px] sm:text-xs mt-3" style={{ color: 'var(--text-muted)' }}>
            {t('Already have it, another way?')}{' '}
            {included.map((w, i) => (
              <span key={w.t}>
                {i > 0 ? ` · ` : ''}
                <Link href={w.href} target="_blank" className="font-semibold" style={{ color: 'var(--accent)' }}>{t(w.t)}</Link> {t(w.d)}
              </span>
            ))}
            {t(' — both include everything Zajednica has.')}
          </p>
        </div>

        <div className="mt-4 text-[13px] sm:text-xs flex flex-col gap-1" style={{ color: 'var(--text-muted)' }}>
          <p className="flex items-center gap-1.5">
            <ExternalLink size={11} className="shrink-0" />
            {t("Looking for signals, not coaching? FX Doctor's Gold")} <Link href={`/channels/${FXDOCTOR_GOLD_CHANNEL_ID}`} className="font-semibold" style={{ color: 'var(--accent)' }}>{t('(XAUUSD, $100/month) →')}</Link> {t('is a separate ShowMeTrade channel — subscribers get this page automatically, no request needed.')}
          </p>
          <p className="flex items-center gap-1.5 pl-[19px]">
            {t('Prefer to pay Gold by direct link?')} <Link href={FXDOCTOR_GOLD_STRIPE_LINK} target="_blank" className="font-semibold" style={{ color: 'var(--accent)' }}>{t('Pay on Stripe →')}</Link> {t("Sign in here and request access below afterwards (it isn't automatic for this link).")}
          </p>
        </div>

        <div className="mt-6 rounded-2xl p-5" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
          {!signedIn ? (
            <>
              <div className="text-[16px] sm:text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{t('Already part of the community?')}</div>
              <p className="text-[14px] sm:text-xs mt-1 mb-3" style={{ color: 'var(--text-secondary)' }}>{t('Sign in (free, 30 seconds) and request access.')}</p>
              <Link href="/sign-up-login-screen?next=%2Flive" className="btn-primary inline-flex px-4 py-3 text-[15px] sm:text-sm">{t('Sign in and request access')}</Link>
            </>
          ) : request?.status === 'PENDING' ? (
            <div className="flex items-start gap-3">
              <Clock size={18} style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 2 }} />
              <div>
                <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{t('Request sent — waiting for approval.')}</div>
                <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>{t('You will see the rooms here as soon as @fxdoctor confirms. Usually within a few hours.')}</p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit}>
              {request?.status === 'REJECTED' && (
                <p className="text-xs mb-3 px-3 py-2 rounded-lg" style={{ background: 'var(--bearish-muted)', color: 'var(--bearish)' }}>
                  {t('Your previous request was not approved.')}{request.adminNote ? ` ${request.adminNote}` : ''} {t('You can send a new one with more details.')}
                </p>
              )}
              <div className="text-sm font-bold mb-3" style={{ color: 'var(--text-primary)' }}>{t('Request access')}</div>
              <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>{t('How are you part of the community?')}</label>
              <select value={kind} onChange={(e) => setKind(e.target.value as CommunityKind)} className="input-base w-full px-3 py-2.5 text-sm">
                {COMMUNITY_KINDS.map((k) => <option key={k.value} value={k.value}>{t(k.label)}</option>)}
              </select>
              <label className="block text-xs font-semibold mt-3 mb-1" style={{ color: 'var(--text-secondary)' }}>{t(hint)}</label>
              <input value={reference} onChange={(e) => setReference(e.target.value)} maxLength={120} className="input-base w-full px-3 py-2.5 text-sm font-mono-nums" placeholder={kind === 'PUPRIME' ? '34884663' : ''} />
              <label className="block text-xs font-semibold mt-3 mb-1" style={{ color: 'var(--text-secondary)' }}>{t('Note (optional)')}</label>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} rows={2} className="input-base w-full px-3 py-2.5 text-sm" />
              {error && <p className="text-xs mt-2" style={{ color: 'var(--bearish)' }}>{error}</p>}
              <button type="submit" disabled={busy} className="btn-primary mt-3 inline-flex items-center gap-2 px-4 py-2.5 text-sm disabled:opacity-50">
                <CheckCircle2 size={14} /> {busy ? t('Sending…') : t('Send request')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
